'use client';
import { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import type { FieldError, FieldValues, UseControllerProps } from 'react-hook-form';
import formStyle from '../../../styles/modules/form.module.scss';
import type { VariantType } from '../../../types';
import FormLabel from '../FormLabel/FormLabel';
import fanychanceStyles from './FormTextArea.fanychance.module.scss';
import styles from './FormTextArea.module.scss';
import raffleStyles from './FormTextArea.raffle.module.scss';

export type FormTextAreaProps<T extends FieldValues> = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  rows?: number;
  prefix?: React.ReactNode;
  outlined?: boolean;
  onChange?: () => void;
  disabled?: boolean;
  hint?: string;
  variant?: VariantType;
} & UseControllerProps<T>;

function FormTextArea<T extends FieldValues>({
  name,
  label,
  placeholder,
  required = false,
  readOnly = false,
  rows = 5,
  control,
  rules,
  prefix,
  outlined = false,
  onChange,
  disabled = false,
  hint,
  variant = 'default',
}: FormTextAreaProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control, rules });
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState(100);

  useEffect(() => {
    if (textAreaRef.current) {
      const scrollHeight = textAreaRef.current.scrollHeight;
      //下限100px、上限500px
      if (scrollHeight < 100) {
        setHeight(100);
      } else if (scrollHeight > 500) {
        setHeight(500);
      } else if (height < scrollHeight) {
        // 伸びる方向にのみ高さを変更する
        setHeight(scrollHeight);
      }
    }
  }, [field.value]);

  // variantに応じてクラス名を決定
  const getVariantStyles = () => {
    switch (variant) {
      case 'raffle':
        return raffleStyles;
      case 'fanychance':
        return fanychanceStyles;
      default:
        return styles;
    }
  };

  const currentStyles = getVariantStyles();
  const inputClassName = currentStyles.input;
  const wrapperClassName = currentStyles.wrapper;

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && <FormLabel>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error && formStyle.hasError}>
        <div className={`${wrapperClassName} ${outlined && styles.outlined}`}>
          {prefix && <div className={formStyle.prefix}>{prefix}</div>}
          <textarea
            ref={textAreaRef}
            readOnly={readOnly}
            required={required}
            name={field.name}
            placeholder={placeholder}
            rows={rows}
            className={inputClassName}
            onChange={(e) => {
              field.onChange(e);
              if (onChange) onChange();
            }}
            style={{ height: height ? `${height}px` : 'auto' }}
            value={field.value}
            disabled={disabled}
          />
        </div>
        {hint && <p className={formStyle.hint}>{hint}</p>}
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as FieldError)?.message}</p>}
      </div>
    </div>
  );
}

export default FormTextArea;
